import { jsx } from 'react/jsx-runtime';
import { useToast } from '@flex-design-system/fx';
import { useTranslation } from '@flex-mf/i18n';
import { ServerSideSuspense, ErrorBoundary } from '@flexteam/shared-react';
import { useCallback } from 'react';
import DelayedSuspense from '../DelayedSuspense/index.mjs';
import { handleCustomError } from '../utils.mjs';
import ErrorView from './ErrorView.mjs';

const defaultRejectedFallback = ({
  resetError
}) => {
  return /* @__PURE__ */ jsx(ErrorView, { onClickRetry: resetError });
};
function AsyncBoundary({
  fallbackConfig,
  pendingFallback,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx(Inner, { ...props, children: fallbackConfig ? /* @__PURE__ */ jsx(
    DelayedSuspense,
    {
      fallbackConfig,
      fallback: pendingFallback,
      children
    }
  ) : /* @__PURE__ */ jsx(ServerSideSuspense, { fallback: pendingFallback, children }) });
}
function Inner({
  rejectedFallback = defaultRejectedFallback,
  children,
  onUnhandledError: onUnhandledErrorFromProps,
  ...errorBoundaryProps
}) {
  const toast = useToast();
  const { t } = useTranslation();
  const defaultErrorHandler = useCallback(
    ({ httpError }) => {
      toast.error(
        /* @__PURE__ */ jsx("div", { style: { whiteSpace: "pre-line" }, children: httpError?.message ?? t("global.unknown_error_message", {
          ns: "@flex-services/async-boundary"
        }) })
      );
    },
    [t, toast]
  );
  const onUnhandledError = onUnhandledErrorFromProps ?? defaultErrorHandler;
  return /* @__PURE__ */ jsx(
    ErrorBoundary,
    {
      renderFallback: rejectedFallback,
      onError: ({ error }) => {
        const { isHandled, httpError } = handleCustomError(error);
        if (!isHandled) {
          onUnhandledError({ thrownError: error, httpError });
        }
      },
      ...errorBoundaryProps,
      children
    }
  );
}
AsyncBoundary.ErrorBoundary = Inner;
AsyncBoundary.ErrorView = ErrorView;

export { AsyncBoundary as default };
//# sourceMappingURL=index.mjs.map
